import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cn } from "@/lib/utils";

export interface ProgressSegment {
  value: number;
  color: string;
  label?: string;
}

interface MultiProgressProps extends 
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  segments: ProgressSegment[];
  showLabels?: boolean;
}

const MultiProgress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  MultiProgressProps
>(({ className, segments, showLabels = false, ...props }, ref) => {
  // Calculate total value to ensure it doesn't exceed 100%
  const totalValue = segments.reduce((sum, segment) => sum + segment.value, 0);
  
  // Adjust segment values if total exceeds 100%
  const adjustedSegments = totalValue > 100 
    ? segments.map(segment => ({
        ...segment,
        value: (segment.value / totalValue) * 100
      }))
    : segments;

  return (
    <div className="space-y-1.5">
      <ProgressPrimitive.Root
        ref={ref}
        className={cn(
          "relative h-4 w-full overflow-hidden rounded-full bg-secondary",
          className
        )}
        {...props}
      >
        <div className="flex h-full w-full">
          {adjustedSegments.map((segment, index) => (
            <div
              key={index}
              className="h-full transition-all"
              style={{ 
                width: `${segment.value}%`,
                backgroundColor: segment.color
              }}
            />
          ))}
        </div>
      </ProgressPrimitive.Root>
      
      {showLabels && (
        <div className="flex text-xs">
          {adjustedSegments.map((segment, index) => (
            <div 
              key={index}
              className="flex items-center mr-4"
            >
              <div 
                className="w-2 h-2 rounded-full mr-1"
                style={{ backgroundColor: segment.color }}
              />
              <span>{segment.label || `Segment ${index + 1}`}: {segment.value.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

MultiProgress.displayName = "MultiProgress";

export { MultiProgress }; 